/**
 * PostHog Fast Feature Flags (PFFF) — client-side feature flag assignment.
 *
 * Replicates PostHog's /decide hashing algorithm in the browser so flags are
 * available synchronously before posthog.init() completes its network call.
 *
 * Source: https://posthog-fast-feature-flags.vercel.app/
 */

interface FlagConfig {
  key: string;
  variants: Record<string, number>;
}

// ── SHA-1 (pure JS, same as PostHog backend) ────────────────────────────────

function rotl(x: number, n: number): number {
  return ((x << n) | (x >>> (32 - n))) >>> 0;
}

function sha1Prefix15(input: string): string {
  let h0 = 0x67452301;
  let h1 = 0xefcdab89;
  let h2 = 0x98badcfe;
  let h3 = 0x10325476;
  let h4 = 0xc3d2e1f0;

  const bytes: number[] = [];
  for (let i = 0; i < input.length; i++) {
    bytes.push(input.charCodeAt(i) & 0xff);
  }
  bytes.push(0x80);
  while ((bytes.length * 8) % 512 !== 448) bytes.push(0);

  const bitLen = input.length * 8;
  bytes.push(0, 0, 0, 0);
  bytes.push((bitLen >>> 24) & 0xff);
  bytes.push((bitLen >>> 16) & 0xff);
  bytes.push((bitLen >>> 8) & 0xff);
  bytes.push(bitLen & 0xff);

  for (let offset = 0; offset < bytes.length; offset += 64) {
    const w = new Array<number>(80);
    for (let t = 0; t < 16; t++) {
      w[t] =
        (bytes[offset + t * 4] << 24) |
        (bytes[offset + t * 4 + 1] << 16) |
        (bytes[offset + t * 4 + 2] << 8) |
        bytes[offset + t * 4 + 3];
    }
    for (let t = 16; t < 80; t++) {
      w[t] = rotl(w[t - 3] ^ w[t - 8] ^ w[t - 14] ^ w[t - 16], 1);
    }

    let [a, b, c, d, e] = [h0, h1, h2, h3, h4];
    for (let t = 0; t < 80; t++) {
      const f =
        t < 20
          ? (b & c) | (~b & d)
          : t < 40
            ? b ^ c ^ d
            : t < 60
              ? (b & c) | (b & d) | (c & d)
              : b ^ c ^ d;
      const k =
        t < 20
          ? 0x5a827999
          : t < 40
            ? 0x6ed9eba1
            : t < 60
              ? 0x8f1bbcdc
              : 0xca62c1d6;
      const temp = (rotl(a, 5) + f + e + k + w[t]) >>> 0;
      e = d;
      d = c;
      c = rotl(b, 30);
      b = a;
      a = temp;
    }

    h0 = (h0 + a) >>> 0;
    h1 = (h1 + b) >>> 0;
    h2 = (h2 + c) >>> 0;
    h3 = (h3 + d) >>> 0;
    h4 = (h4 + e) >>> 0;
  }

  return [h0, h1, h2, h3, h4]
    .map((v) => v.toString(16).padStart(8, "0"))
    .join("")
    .slice(0, 15);
}

// ── Hashing & variant selection ─────────────────────────────────────────────

function hashToFloat(flagKey: string, identity: string, suffix = ""): number {
  const hashKey = `${flagKey}.${identity}${suffix}`;
  const hex = sha1Prefix15(hashKey);
  const num = BigInt(`0x${hex}`);
  const max = BigInt("0xFFFFFFFFFFFFFFF");
  return Number(num) / Number(max);
}

function assignVariant(
  identity: string,
  flag: FlagConfig
): string | null {
  const ranges: { key: string; min: number; max: number }[] = [];
  let cursor = 0;
  for (const [key, weight] of Object.entries(flag.variants)) {
    const end = cursor + weight;
    ranges.push({ key, min: cursor, max: end });
    cursor = end;
  }

  const value = hashToFloat(flag.key, identity, "variant");
  for (const r of ranges) {
    if (value >= r.min && value < r.max) return r.key;
  }
  return null;
}

// ── Identity (cookie-based, consent-aware) ──────────────────────────────────

let memoryId: string | undefined;

function readCookieIdentity(): string | undefined {
  if (typeof document === "undefined") return undefined;

  const cookie = document.cookie
    .split(";")
    .find((c) => c.trim().startsWith("pfff="));

  if (!cookie) return undefined;

  const eqIdx = cookie.indexOf("=");
  if (eqIdx === -1) return undefined;

  const raw = cookie.slice(eqIdx + 1).trim();
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

function generateId(): string {
  return Math.random().toString(36).substring(2) + Date.now().toString(36);
}

/**
 * Return a stable visitor identity. Reads from the pfff cookie if one exists,
 * otherwise generates an in-memory ID (no cookie written — call
 * persistIdentity() after consent to persist it).
 */
function getIdentity(): string {
  const existing = readCookieIdentity();
  if (existing) return existing;

  if (!memoryId) memoryId = generateId();
  return memoryId;
}

/**
 * Persist the current identity to a cookie. Call this only after the user
 * has granted consent (e.g. via Termly).
 */
export function persistIdentity(): void {
  if (typeof document === "undefined") return;

  const id = getIdentity();
  document.cookie = `pfff=${encodeURIComponent(id)};path=/;max-age=31536000;SameSite=Strict;Secure`;
}

/**
 * Remove the persisted identity cookie. Call this when the user revokes
 * consent so no persistent identifier remains.
 */
export function clearPersistedIdentity(): void {
  if (typeof document === "undefined") return;

  document.cookie = "pfff=;path=/;max-age=0;SameSite=Strict;Secure";
  memoryId = undefined;
}

// ── Public API ──────────────────────────────────────────────────────────────

export function evaluateFlags(
  flags: FlagConfig[]
): Record<string, string | boolean> {
  const identity = getIdentity();
  const result: Record<string, string | boolean> = {};

  for (const flag of flags) {
    const entries = Object.entries(flag.variants);
    for (const [variantKey, weight] of entries) {
      if (!Number.isFinite(weight) || weight < 0) {
        throw new Error(
          `Variant "${variantKey}" of flag "${flag.key}" has invalid weight: ${weight}`
        );
      }
    }
    const total = entries.reduce((s, [, v]) => s + v, 0);
    if (Math.abs(total - 1) > 0.0001) {
      throw new Error(
        `Variants for flag "${flag.key}" must sum to 1, got ${total}`
      );
    }
    const assigned = assignVariant(identity, flag);
    if (assigned !== null) result[flag.key] = assigned;
  }

  return result;
}

export function pfffIdentity(): string {
  return getIdentity();
}
